<template>
  <div class="block mx-auto">
    <div class="bg-grey-background-subdued sm:bg-white sm:box-shadow-sm p-16">
      <p class="text-overline text-grey-header">
        {{ isAdditionalProfile ? 'Profile added!' : "You're in!" }}
      </p>
      <div class="flex gap-16 mt-16">
        <ProgressBar :value="1" :max="1" :content="isAdditionalProfile ? 'Profile added!' : 'You\'re in!'" />
        <ProgressBar :value="1" :max="1" content="" />
      </div>
    </div>
    <div class="mx-auto lg:max-w-[986px]">
      <h1 class="text-h1 mt-24 mb-24 lg:mb-48 px-16">
        {{ isAdditionalProfile ? `You're ready to find new opportunities` : "You're ready to start applying to jobs" }}
      </h1>
      <div class="flex flex-col-reverse gap-[24px] lg:flex-row lg:gap-[48px]">
        <ProfileCompleteActions
          :isAdditionalProfile="isAdditionalProfile"
          :hasPhoto="hasPhoto"
          :formattedCareType="formattedCareType"
          :jobSearchLink="jobSearchLink"
          :profileEditLink="profileEditLink"
        />
        <ProfileCompletePreview
          :hasCardData="hasCardData"
          :sitterCardName="sitterCardName"
          :yearsExperience="yearsExperience"
          :city="city"
          :stateAbbr="stateAbbr"
          :bio="bio"
          :idv="idv"
          :badges="badges"
          :minRate="minRate"
          :maxRate="maxRate"
          :hasPhoto="hasPhoto"
          :photoUrl="account?.croppedPhoto"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import snakeCase from 'lodash/snakeCase';
import ProgressBar from '@/components/progress_bar/ProgressBar.vue';
import ProfileCompleteActions from './ProfileCompleteActions.vue';
import ProfileCompletePreview from './ProfileCompletePreview.vue';
import { sendSegmentEvent } from '@/lib/segment';
import { readDomainCookie, deleteDomainCookie } from '@/lib/domain_cookie';
import { IDV, Badge } from '@/components/sitter_badge_list/__types__/SitterBadgeListTypes';

export default {
  name: 'ProfileComplete',
  components: {
    ProgressBar,
    ProfileCompleteActions,
    ProfileCompletePreview,
  },
  computed: {
    account() {
      return this.$store.getters['account/getAccount'];
    },
    isAdditionalProfile(): Boolean {
      return readDomainCookie('additional_profile') === 'true';
    },
    formattedCareType(): String {
      return this.sanitizedCareType.split('_').join(' ');
    },
    yearsExperience(): Number {
      try {
        return Number(this.$store.getters.getFormField('yearsExperience'));
      } catch {
        return 0;
      }
    },
    careType(): String {
      return this.$store.getters.careType || 'babysitter';
    },
    sanitizedCareType(): String {
      return snakeCase(this.$store.getters.careType) || 'babysitter';
    },
    bio(): String {
      try {
        return this.$store.getters.getFormField('bio');
      } catch {
        return '';
      }
    },
    idv(): IDV {
      // Always return 'passed' for IDV since users only reach this page after passing IDV
      return { verdict: 'passed' };
    },
    badges(): Badge[] {
      const badges: Badge[] = [];
      if (this.$store.getters.getFormField('qualifications')?.includes('CPR_CERTIFIED')) {
        badges.push({ name: 'cpr_certified', status: 'COMPLETE' });
      }
      if (this.$store.getters.getFormField('qualifications')?.includes('FIRST_AID_TRAINING')) {
        badges.push({ name: 'first_aid_training', status: 'COMPLETE' });
      }
      return badges;
    },
    minRate(): Number {
      try {
        return this.$store.getters.minPaymentRate * 100;
      } catch {
        return 0;
      }
    },
    maxRate(): Number {
      try {
        return this.$store.getters.maxPaymentRate * 100;
      } catch {
        return 0;
      }
    },
    sitterCardName(): String {
      return `${this.account?.firstName ?? ''} ${this.account?.lastName?.[0] ?? ''}`;
    },
    profileEditLink(): String {
      return `/profile/provider/${this.sanitizedCareType}/edit_entry`;
    },
    jobSearchLink(): String {
      return `/provider/jobs/search?careType=${this.sanitizedCareType}&zipcode=${this.zipCode}`;
    },
    hasCardData(): Boolean {
      return !!(this.$store.state.sitterOnboarding && this.account && this.bio);
    },
    hasPhoto(): Boolean {
      return !!this.account?.croppedPhoto;
    },
    zipCode(): String {
      return this.account?.location?.zipcode || '';
    },
    stateAbbr(): String {
      return this.account?.location?.stateabbr || '';
    },
    city(): String {
      return this.account?.location?.cityname || '';
    },
  },
  mounted() {
    this.blurAllCards();

    if (readDomainCookie('profile_completed')) {
      if (this.isAdditionalProfile) {
        this.trackSitterAdditionalProfileCompleted();
      } else {
        this.trackSitterProfileCompleted();
      }
    }
  },
  methods: {
    trackSitterProfileCompleted() {
      deleteDomainCookie('profile_completed');
      sendSegmentEvent('Sitter Profile Completed', {
        profile_type: this.careType,
      });
    },
    trackSitterAdditionalProfileCompleted() {
      deleteDomainCookie('profile_completed');
      deleteDomainCookie('additional_profile');
      sendSegmentEvent('Sitter Additional Profile Completed', {
        profile_type: this.careType,
      });
    },
    blurAllCards() {
      this.$nextTick(() => {
        // Blur all cards to remove focus indicator
        if (document.activeElement) {
          (document.activeElement as HTMLElement)?.blur();
        }
      });
    },
  },
};
</script>
